import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Plus, Minus, X, Search, Phone, Heart, ShoppingBag,
  User, Mail, MapPin, Package, Menu, MessageCircle
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { allProducts, API_URL } from '../data/products';
import ContactPopup from '../components/ContactPopup';
import Chatbot from '../components/Chatbot';

export default function Layout() {
  const location = useLocation();
  const currentPath = location.pathname;

  const {
    cart, isCartOpen, setIsCartOpen, isCheckoutOpen, setIsCheckoutOpen,
    checkoutForm, setCheckoutForm, updateQuantity, removeFromCart,
    getTotal, cartCount, handleCheckout, handlePlaceOrder,
    confirmedOrder, setConfirmedOrder,
    wishlist, notification, isMobile, isChatbotOpen, setIsChatbotOpen,
    isContactPopupOpen, setIsContactPopupOpen,
    searchQuery, setSearchQuery, selectedCategory, setSelectedCategory,
    showNotification,
  } = useApp();

  // ── Google Tag Manager ──
  useEffect(() => {
    // Inject GTM <script> into <head>
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WFBCCBKX');`;
    document.head.insertBefore(gtmScript, document.head.firstChild);

    // Inject GTM <noscript> immediately after <body> opens
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-WFBCCBKX';
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }, []);
  // ── End Google Tag Manager ──

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [autofillSuggestion, setAutofillSuggestion] = useState(null);
  const [billingSame, setBillingSame] = useState(true);
  const [billingForm, setBillingForm] = useState({ fullName: '', address: '', city: '', state: '', pincode: '', phone: '' });
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [checkoutKey, setCheckoutKey] = useState(0);

  // ── Auth modal state ──
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gudy_user') || 'null'); } catch { return null; }
  });
  const [totalOrders, setTotalOrders] = useState(null);

  const ADMIN_EMAIL = 'office.gudy@gmail.com';
  const isAdmin = currentUser?.email === ADMIN_EMAIL;

  // ── Fetch total orders count for admin badge ──
  useEffect(() => {
    if (!isAdmin) { setTotalOrders(null); return; }
    const token = localStorage.getItem('gudy_token');
    fetch(`${API_URL}/admin/orders`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setTotalOrders(data.orders?.length ?? 0))
      .catch(() => setTotalOrders(0));
  }, [isAdmin]);

  // ── Bump key to re-mount form fresh on each checkout open (enables browser autofill) ──
  useEffect(() => {
    if (isCheckoutOpen) setCheckoutKey(k => k + 1);
  }, [isCheckoutOpen]);
  useEffect(() => {
    const key = checkoutForm.email?.trim() || checkoutForm.phone?.trim();
    if (!key || key.length < 6) { setAutofillSuggestion(null); return; }
    try {
      const saved = JSON.parse(localStorage.getItem('gudy_checkout_profiles') || '[]');
      const match = saved.find(p =>
        p.email?.toLowerCase() === checkoutForm.email?.toLowerCase().trim() ||
        p.phone?.trim() === checkoutForm.phone?.trim()
      );
      if (match) setAutofillSuggestion(match);
      else setAutofillSuggestion(null);
    } catch { setAutofillSuggestion(null); }
  }, [checkoutForm.email, checkoutForm.phone]);

  // ── Apply autofill suggestion ──
  const applyAutofill = () => {
    if (!autofillSuggestion) return;
    setCheckoutForm(prev => ({
      ...prev,
      fullName: autofillSuggestion.fullName || prev.fullName,
      email:    autofillSuggestion.email    || prev.email,
      phone:    autofillSuggestion.phone    || prev.phone,
      address:  autofillSuggestion.address  || prev.address,
      city:     autofillSuggestion.city     || prev.city,
      state:    autofillSuggestion.state    || prev.state,
      pincode:  autofillSuggestion.pincode  || prev.pincode,
    }));
    setAutofillSuggestion(null);
    showNotification('Details filled from saved profile!');
  };

  // ── Autofill: fetch City & State from India Post API on 6-digit pincode ──
  useEffect(() => {
    const pin = checkoutForm.pincode?.trim();
    if (!/^\d{6}$/.test(pin)) return;
    setPincodeLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${pin}`)
      .then(r => r.json())
      .then(data => {
        const post = data?.[0]?.PostOffice?.[0];
        if (post) {
          setCheckoutForm(prev => ({
            ...prev,
            city:  prev.city  || post.District || '',
            state: prev.state || post.State    || '',
          }));
        }
      })
      .catch(() => {})
      .finally(() => setPincodeLoading(false));
  }, [checkoutForm.pincode]);

  const navigate = useNavigate();

 
  // ── Admin route guard ──
  useEffect(() => {
    if (currentPath.startsWith('/admin')) {
      const storedUser = JSON.parse(localStorage.getItem('gudy_user') || 'null');
      if (!storedUser || storedUser.email !== ADMIN_EMAIL) {
        navigate('/');
      }
    }
  }, [currentPath, navigate]);

  // ── Auth handlers ──

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/signup';
      const body = authMode === 'login'
        ? { email: authForm.email, password: authForm.password }
        : { name: authForm.name, email: authForm.email, password: authForm.password };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Something went wrong');

      localStorage.setItem('gudy_token', data.token);
      localStorage.setItem('gudy_user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      setIsAuthOpen(false);
      setAuthForm({ name: '', email: '', password: '' });

      // ── Admin redirect ──
      if (data.user.email === ADMIN_EMAIL) {
        showNotification('Welcome, Admin! 🛡️');
        navigate('/admin/orders');
      } else {
        showNotification(`Welcome${authMode === 'signup' ? ', ' + data.user.name : ' back, ' + data.user.name}! 🎉`);
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('gudy_token');
    localStorage.removeItem('gudy_user');
    setCurrentUser(null);
    showNotification('Logged out successfully');
  };

  const navLinkStyle = (path) => ({
    color: currentPath === path ? '#6B4423' : '#333',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: currentPath === path ? '700' : '500',
    borderBottom: currentPath === path ? '2px solid #6B4423' : '2px solid transparent',
    paddingBottom: '2px',
  });

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#f5f5f5', minHeight: '100vh', position: 'relative' }}>

      {/* ── Notification ── */}
      {notification && (
        <div style={{
          position: 'fixed', top: '100px', right: '30px', background: '#6B4423',
          color: 'white', padding: '16px 24px', borderRadius: '12px', zIndex: 10000,
          boxShadow: '0 8px 30px rgba(139,69,19,0.3)'
        }}>
          {notification}
        </div>
      )}

      {/* ── Sticky Header ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 1000, background: 'white' }}>

        {/* Header */}
        <header style={{ background: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', width: '100%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '8px 12px' : '10px 40px', display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '28px' }}>

            <Link to="/" style={{ display: 'flex', flexShrink: 0 }}>
              <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png"
                width={isMobile ? 110 : 220} height={isMobile ? 56 : 112} alt="GUDY Logo" style={{ objectFit: 'contain', width: isMobile ? '110px' : '220px', height: 'auto' }} />
            </Link>

            <div className="desktop-search" style={{ flex: 1, maxWidth: '560px', position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={18} style={{ position: 'absolute', left: '14px', color: '#999' }} />
              <input type="text" placeholder="Search our store" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '10px 48px 10px 44px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
              <button style={{ position: 'absolute', right: '8px', background: '#6B4423', color: 'white', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer' }}>
                <Search size={16} />
              </button>
            </div>

            <div className="desktop-actions" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {currentUser ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#6B4423' }}>Hi, {currentUser.name.split(' ')[0]}</span>
                  <button onClick={handleLogout} style={{ background: 'transparent', border: '1.5px solid #6B4423', color: '#6B4423', borderRadius: '8px', padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    Logout
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => { setAuthMode('login'); setAuthError(''); setIsAuthOpen(true); }}
                    style={{ background: 'transparent', border: '1.5px solid #6B4423', color: '#6B4423', borderRadius: '8px', padding: '7px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <User size={16} /> Login
                  </button>
                  <button onClick={() => { setAuthMode('signup'); setAuthError(''); setIsAuthOpen(true); }}
                    style={{ background: 'linear-gradient(135deg,#8B5A3C,#6B4423)', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    Sign Up
                  </button>
                </div>
              )}

              <button style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                <Heart size={28} />
                {wishlist.length > 0 && (
                  <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#D4C4A8', color: '#6B4423', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {wishlist.length}
                  </span>
                )}
              </button>

              <button onClick={() => setIsCartOpen(true)} style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                <ShoppingBag size={28} />
                {cartCount > 0 && (
                  <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#D4C4A8', color: '#6B4423', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile-only: Cart + User icons */}
            <div className="mobile-actions" style={{ alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
              {/* User / Login icon */}
              {currentUser ? (
                <button onClick={handleLogout}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', gap: '4px', color: '#6B4423', fontSize: '12px', fontWeight: '700' }}>
                  <User size={22} style={{ color: '#6B4423' }} />
                </button>
              ) : (
                <button onClick={() => { setAuthMode('login'); setAuthError(''); setIsAuthOpen(true); }}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                  <User size={22} style={{ color: '#6B4423' }} />
                </button>
              )}
              <button onClick={() => setIsCartOpen(true)}
                style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
                <ShoppingBag size={26} style={{ color: '#6B4423' }} />
                {cartCount > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-2px', background: '#D4C4A8', color: '#6B4423', borderRadius: '50%', width: '20px', height: '20px', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {cartCount}
                  </span>
                )}
              </button>
            </div>

            <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <Menu size={28} style={{ color: '#6B4423' }} />
            </button>
          </div>

          {/* Mobile Search */}
          <div className="mobile-search" style={{ padding: '0 12px 12px' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={20} style={{ position: 'absolute', left: '12px', color: '#999' }} />
              <input type="text" placeholder="Search our store" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '12px 12px 12px 40px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', outline: 'none' }} />
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="desktop-nav" style={{ background: '#f8f8f8', borderTop: '1px solid #e0e0e0', width: '100%' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '10px 40px', display: 'flex', gap: '28px', alignItems: 'center' }}>

              <Link to="/" style={navLinkStyle('/')}>Home</Link>

              <Link to="/about" style={navLinkStyle('/about')}>About</Link>
              <Link to="/contact" style={navLinkStyle('/contact')}>Contact</Link>
              <Link to="/distributor" style={navLinkStyle('/distributor')}>Distributor Enquiry</Link>
              <Link to="/wholesale" style={{ ...navLinkStyle('/wholesale'), background: currentPath === '/wholesale' ? 'transparent' : 'linear-gradient(90deg,#FF9500,#FF6B00)', color: currentPath === '/wholesale' ? '#6B4423' : 'white', padding: '7px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', border: 'none', borderBottom: currentPath === '/wholesale' ? '2px solid #6B4423' : '2px solid transparent', whiteSpace: 'nowrap' }}>📦 Wholesale</Link>

              {/* Admin-only nav link */}
              {isAdmin && (
                <Link to="/admin/orders" style={{
                  ...navLinkStyle('/admin/orders'),
                  background: currentPath === '/admin/orders' ? '#2C1A0E' : 'linear-gradient(135deg,#2C1A0E,#6B4423)',
                  color: 'white', padding: '7px 16px', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '700', border: 'none',
                  borderBottom: '2px solid transparent', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '7px',
                }}>
                  🛒 Total Orders
                  {totalOrders !== null && (
                    <span style={{ background: '#FF9500', color: '#2C1A0E', borderRadius: '20px', padding: '1px 8px', fontSize: '12px', fontWeight: '900' }}>
                      {totalOrders}
                    </span>
                  )}
                </Link>
              )}

              <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer"
                style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto', textDecoration: 'none' }}>
                <Phone size={20} /> Whatsapp Order
              </a>

            </div>
          </nav>

          {/* Mobile Nav Menu */}
          {isMobileMenuOpen && (
            <div className="mobile-nav" style={{
              background: 'white',
              borderTop: '1px solid #e0e0e0',
              padding: '16px 20px 24px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              animation: 'slideDown 0.22s ease-out',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {[
                  { to: '/',            label: 'Home' },
                  { to: '/about',       label: 'About' },
                  { to: '/contact',     label: 'Contact' },
                  { to: '/distributor', label: 'Distributor Enquiry' },
                  { to: '/track-order', label: 'Track Order' },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                      color: currentPath === to ? '#6B4423' : '#333',
                      textDecoration: 'none', fontSize: '16px',
                      fontWeight: currentPath === to ? '700' : '500',
                      padding: '13px 4px',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'block',
                    }}>{label}</Link>
                ))}

                <Link to="/wholesale" onClick={() => setIsMobileMenuOpen(false)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(90deg,#FF9500,#FF6B00)', color: 'white', textDecoration: 'none', fontSize: '15px', fontWeight: '700', padding: '13px 16px', borderRadius: '10px', margin: '8px 0 4px' }}>
                  📦 Wholesale Enquiry
                  <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: '6px', marginLeft: 'auto' }}>₹52/kg</span>
                </Link>

                {/* Admin-only mobile link */}
                {isAdmin && (
                  <Link to="/admin/orders" onClick={() => setIsMobileMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#2C1A0E,#6B4423)', color: 'white', textDecoration: 'none', fontSize: '15px', fontWeight: '700', padding: '13px 16px', borderRadius: '10px', margin: '4px 0' }}>
                    <span>🛒 Total Orders</span>
                    {totalOrders !== null && (
                      <span style={{ background: '#FF9500', color: '#2C1A0E', borderRadius: '20px', padding: '2px 10px', fontSize: '13px', fontWeight: '900' }}>{totalOrders}</span>
                    )}
                  </Link>
                )}

                {currentUser && (
                  <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    style={{ background: 'transparent', border: '2px solid #6B4423', color: '#6B4423', borderRadius: '10px', padding: '12px 16px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', textAlign: 'center', marginTop: '4px' }}>
                    Logout ({currentUser.name.split(' ')[0]})
                  </button>
                )}

                <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer"
                  style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '8px', padding: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginTop: '8px', textDecoration: 'none' }}>
                  <Phone size={18} /> Whatsapp Order
                </a>
              </div>
            </div>
          )}
        </header>
      </div>


      {/* ── Cart Sidebar ── */}
      {isCartOpen && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} onClick={() => setIsCartOpen(false)} />
          <div style={{ position: 'fixed', top: 0, right: 0, width: '450px', maxWidth: '100%', height: '100%', background: 'white', zIndex: 9999, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
            <div style={{ padding: '30px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700' }}>Cart</h2>
              <button onClick={() => setIsCartOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px' }}><X size={24} /></button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '20px 30px' }}>
              {cart.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '20px' }}>
                  <ShoppingCart size={64} style={{ color: '#ccc' }} />
                  <p style={{ fontSize: '18px', color: '#999' }}>Your cart is empty</p>
                </div>
              ) : (
                cart.map(item => (
                  <React.Fragment key={item.id}>
                    <div style={{ display: 'flex', gap: '16px', padding: '20px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <img src={item.image} alt={item.name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px' }} />
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>{item.name}</h3>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>{item.weight}</p>
                       
                        <p style={{ fontSize: '18px', fontWeight: '700', color: '#6B4423' }}>₹{item.priceINR}</p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f8f8f8', borderRadius: '8px', padding: '4px' }}>
                          <button onClick={() => updateQuantity(item.id, -1)} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Minus size={16} /></button>
                          <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '24px', textAlign: 'center' }}>{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Plus size={16} /></button>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} style={{ background: '#fee', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', color: '#c33' }}><X size={20} /></button>
                      </div>
                    </div>
                    {item.freeGift && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '6px', marginBottom: '4px', padding: '8px 12px', background: '#fff8e7', border: '1px dashed #d4a03a', borderRadius: '10px' }}>
                        <span style={{ fontSize: '18px' }}>🎁</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#7a4f00' }}>{item.freeGift.label}</div>
                          <div style={{ fontSize: '11px', color: '#b07d2a' }}>Free with your 750g purchase</div>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d6a4f' }}>FREE</div>
                      </div>
                    )}
                  </React.Fragment>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div style={{ padding: '24px 30px 30px', borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#888' }}>Subtotal</span>
                  <span style={{ fontSize: '14px', color: '#333' }}>₹{getTotal()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <span style={{ fontSize: '14px', color: '#888' }}>Delivery</span>
                  <span style={{ fontSize: '14px', color: '#333' }}>₹50</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', paddingTop: '12px', borderTop: '2px solid #e0e0e0' }}>
                  <span style={{ fontSize: '17px', fontWeight: '700' }}>Total</span>
                  <span style={{ fontSize: '22px', fontWeight: '700', color: '#6B4423' }}>₹{getTotal() + 50}</span>
                </div>
                <button onClick={handleCheckout} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', marginBottom: '10px', letterSpacing: '0.3px' }}>
                  Checkout →
                </button>
                <button onClick={() => setIsCartOpen(false)} style={{ width: '100%', padding: '14px', background: 'white', color: '#6B4423', border: '2px solid #6B4423', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                  ← Continue Shopping
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Checkout Modal (always in DOM so browser remembers autofill) ── */}
      <div style={{ display: isCheckoutOpen ? 'flex' : 'none', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}
        onClick={() => setIsCheckoutOpen(false)}>
        <div className="checkout-modal" style={{ background: 'white', borderRadius: '20px', maxWidth: '1000px', width: '95%', maxHeight: '92vh', overflow: 'auto', position: 'relative', display: 'flex', flexDirection: 'column' }}
          onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="checkout-header" style={{ padding: '20px 32px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
              <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png" height="142px" alt="GUDY" style={{ objectFit: 'contain' }} />
              <button onClick={() => setIsCheckoutOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={22} /></button>
            </div>

            {/* Two-column body */}
            <div className="checkout-two-col" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', flex: 1, minHeight: 0 }}>

              {/* ── Left: Form ── */}
              <form key={checkoutKey} onSubmit={handlePlaceOrder} autoComplete="on" name="checkout-shipping" style={{ padding: '32px', overflowY: 'auto', borderRight: '1px solid #e8e8e8' }}>

                {/* Shipping Information */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Shipping Information</h3>
                  <button
                    type="button"
                    onClick={() => setIsCheckoutOpen(false)}
                    style={{ background: '#f5f5f5', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                  >
                    <X size={16} color="#666" />
                  </button>
                </div>

                {/* Autofill suggestion banner */}
                {autofillSuggestion && (
                  <div style={{ marginBottom: '12px', padding: '12px 16px', background: '#f0faf4', border: '1.5px solid #40916c', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d6a4f' }}>📋 Saved details found</div>
                      <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>{autofillSuggestion.fullName} · {autofillSuggestion.city}, {autofillSuggestion.state}</div>
                    </div>
                    <button
                      type="button"
                      onClick={applyAutofill}
                      style={{ background: '#2d6a4f', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Use these details
                    </button>
                  </div>
                )}

                {/* Logged-in autofill banner */}
                {!autofillSuggestion && currentUser && checkoutForm.email && (
                  <div style={{ marginBottom: '12px', padding: '10px 16px', background: '#f0f4ff', border: '1.5px solid #6B4423', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>✅</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#6B4423' }}>Details auto-filled from your account</div>
                      <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>Review and update if needed before placing order</div>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="email"
                    name="email"
                    id="checkout-email"
                    autoComplete="email"
                    placeholder="Email address"
                    defaultValue={checkoutForm.email}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                    onBlur={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                    style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    required
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555', marginBottom: '28px', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ accentColor: '#6B4423' }} />
                  Email me with news and offers
                </label>

                {/* Delivery */}
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '14px', color: '#1a1a1a' }}>Delivery</h3>
                <div style={{ marginBottom: '12px' }}>
                  <select style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: 'white', color: '#333' }}>
                    <option>India</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    name="given-name"
                    id="checkout-firstname"
                    autoComplete="given-name"
                    placeholder="First name"
                    defaultValue={checkoutForm.fullName?.split(' ')[0] || ''}
                    onBlur={(e) => {
                      const last = checkoutForm.fullName?.split(' ').slice(1).join(' ') || '';
                      setCheckoutForm(prev => ({ ...prev, fullName: (e.target.value + ' ' + last).trim() }));
                    }}
                    style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                    required
                  />
                  <input
                    type="text"
                    name="family-name"
                    id="checkout-lastname"
                    autoComplete="family-name"
                    placeholder="Last name"
                    defaultValue={checkoutForm.fullName?.split(' ').slice(1).join(' ') || ''}
                    onBlur={(e) => {
                      const first = checkoutForm.fullName?.split(' ')[0] || '';
                      setCheckoutForm(prev => ({ ...prev, fullName: (first + ' ' + e.target.value).trim() }));
                    }}
                    style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    name="organization"
                    autoComplete="organization"
                    placeholder="Company (optional)"
                    style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    name="address-line1"
                    id="checkout-address"
                    autoComplete="address-line1"
                    placeholder="Address"
                    defaultValue={checkoutForm.address}
                    onBlur={(e) => setCheckoutForm(prev => ({ ...prev, address: e.target.value }))}
                    style={{ width: '100%', padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    name="city"
                    id="checkout-city"
                    autoComplete="address-level2"
                    placeholder="City"
                    defaultValue={checkoutForm.city}
                    onBlur={(e) => setCheckoutForm(prev => ({ ...prev, city: e.target.value }))}
                    style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                    required
                  />

                  {/* ── Searchable State Dropdown ── */}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="checkout-state"
                      autoComplete="address-level1"
                      placeholder="State"
                      value={stateDropdownOpen ? stateSearch : (checkoutForm.state || '')}
                      onChange={(e) => { setStateSearch(e.target.value); setStateDropdownOpen(true); }}
                      onFocus={() => { setStateSearch(checkoutForm.state || ''); setStateDropdownOpen(true); }}
                      onBlur={() => setTimeout(() => setStateDropdownOpen(false), 150)}
                      style={{ width: '100%', padding: '13px 16px', border: `1.5px solid ${stateDropdownOpen ? '#6B4423' : '#d0d0d0'}`, borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: checkoutForm.state ? '#f0faf4' : 'white' }}
                      required
                    />
                    {stateDropdownOpen && (() => {
                      const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh'];
                      const filtered = STATES.filter(s => s.toLowerCase().startsWith(stateSearch.toLowerCase()));
                      return filtered.length > 0 ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1.5px solid #6B4423', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 9999, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' }}>
                          {filtered.map(s => (
                            <div
                              key={s}
                              onMouseDown={() => {
                                setCheckoutForm(prev => ({ ...prev, state: s }));
                                setStateSearch('');
                                setStateDropdownOpen(false);
                              }}
                              style={{ padding: '11px 16px', fontSize: '14px', cursor: 'pointer', color: checkoutForm.state === s ? '#6B4423' : '#333', fontWeight: checkoutForm.state === s ? '700' : '400', background: checkoutForm.state === s ? '#FDF6EE' : 'white' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#FDF6EE'}
                              onMouseLeave={e => e.currentTarget.style.background = checkoutForm.state === s ? '#FDF6EE' : 'white'}
                            >
                              {s}
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="postal-code"
                      id="checkout-pincode"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="PIN code"
                      defaultValue={checkoutForm.pincode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setCheckoutForm(prev => ({ ...prev, pincode: val, city: '', state: '' }));
                      }}
                      onBlur={(e) => setCheckoutForm(prev => ({ ...prev, pincode: e.target.value }))}
                      style={{ width: '100%', padding: '13px 36px 13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      required
                    />
                    {pincodeLoading && <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px' }}>⏳</span>}
                  </div>
                </div>
                <div style={{ marginBottom: '28px' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="tel"
                      name="tel"
                      id="checkout-phone"
                      autoComplete="tel"
                      placeholder="Phone"
                      defaultValue={checkoutForm.phone}
                      onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                      onBlur={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{ width: '100%', padding: '13px 44px 13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      required
                    />
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '16px' }}>ℹ</span>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555', marginBottom: '32px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ accentColor: '#6B4423' }}
                    onChange={(e) => {
                      if (e.target.checked && checkoutForm.email && checkoutForm.fullName) {
                        try {
                          const existing = JSON.parse(localStorage.getItem('gudy_checkout_profiles') || '[]');
                          const filtered = existing.filter(p =>
                            p.email?.toLowerCase() !== checkoutForm.email?.toLowerCase() &&
                            p.phone !== checkoutForm.phone
                          );
                          filtered.unshift({ ...checkoutForm });
                          localStorage.setItem('gudy_checkout_profiles', JSON.stringify(filtered.slice(0, 5)));
                        } catch {}
                      }
                    }}
                  />
                  Save this information for next time
                </label>

                {/* Payment */}
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '6px', color: '#1a1a1a' }}>Payment</h3>
                <p style={{ fontSize: '13px', color: '#777', marginBottom: '14px' }}>All transactions are secure and encrypted.</p>
                <div style={{ border: '2px solid #F5A623', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px', background: '#FFFBF2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 18px' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#F5A623', border: '2px solid #F5A623', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>Cash on Delivery (COD)</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>Pay when your order arrives</span>
                  </div>
                </div>

                {/* Billing address */}
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '14px', color: '#1a1a1a' }}>Billing address</h3>
                <div style={{ border: '1.5px solid #d0d0d0', borderRadius: '8px', overflow: 'hidden', marginBottom: billingSame ? '28px' : '16px' }}>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', background: billingSame ? '#FFFBF2' : 'white', borderBottom: '1px solid #e8e8e8', transition: 'background 0.15s' }}
                    onClick={() => setBillingSame(true)}
                  >
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${billingSame ? '#F5A623' : '#ccc'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {billingSame && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F5A623' }} />}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: billingSame ? '600' : '400' }}>Same as shipping address</span>
                  </label>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer', background: !billingSame ? '#FFFBF2' : 'white', transition: 'background 0.15s' }}
                    onClick={() => setBillingSame(false)}
                  >
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${!billingSame ? '#F5A623' : '#ccc'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {!billingSame && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F5A623' }} />}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: !billingSame ? '600' : '400' }}>Use a different billing address</span>
                  </label>
                </div>

                {!billingSame && (
                  <div style={{ border: '1.5px solid #e0e0e0', borderRadius: '10px', padding: '20px', marginBottom: '28px', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '2px' }}>Billing Details</div>
                    <input type="text" name="name" autoComplete="name" placeholder="Full name" value={billingForm.fullName} onChange={e => setBillingForm(p => ({ ...p, fullName: e.target.value }))} style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                    <input type="text" name="address-line1" autoComplete="street-address" placeholder="Address" value={billingForm.address} onChange={e => setBillingForm(p => ({ ...p, address: e.target.value }))} style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                      <input type="text" name="address-level2" autoComplete="address-level2" placeholder="City" value={billingForm.city} onChange={e => setBillingForm(p => ({ ...p, city: e.target.value }))} style={{ padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                      <select name="address-level1" autoComplete="address-level1" value={billingForm.state} onChange={e => setBillingForm(p => ({ ...p, state: e.target.value }))} style={{ padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: 'white', color: billingForm.state ? '#333' : '#999' }}>
                        <option value="">State</option>
                        {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input type="text" name="postal-code" autoComplete="postal-code" placeholder="PIN code" inputMode="numeric" maxLength={6} value={billingForm.pincode} onChange={e => setBillingForm(p => ({ ...p, pincode: e.target.value }))} style={{ padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none' }} />
                    </div>
                    <input type="tel" name="tel" autoComplete="tel" placeholder="Phone" value={billingForm.phone} onChange={e => setBillingForm(p => ({ ...p, phone: e.target.value }))} style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #d0d0d0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                )}

                <button type="submit" style={{ width: '100%', padding: '17px', background: '#F5A623', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.3px' }}>
                  Complete order
                </button>
                <p style={{ textAlign: 'center', fontSize: '12px', color: '#aaa', marginTop: '20px' }}>All rights reserved sunrajaoilindustries</p>
              </form>

              {/* ── Right: Order Summary ── */}
              <div style={{ padding: '32px 28px', background: '#f9f9f9', borderRadius: '0 20px 20px 0', overflowY: 'auto' }}>
                {cart.map(item => (
                  <div key={item.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: item.freeGift ? '8px' : '18px' }}>
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <img src={item.image} alt={item.name} style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '10px', border: '1.5px solid #e0e0e0' }} />
                        <span style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#6B4423', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.quantity}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#2C1810', lineHeight: '1.4', marginBottom: '2px' }}>{item.name}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{item.weight}</div>
                        
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', flexShrink: 0 }}>₹{item.priceINR * item.quantity}</div>
                    </div>
                    {/* FREE GIFT row under 750g items */}
                    {item.freeGift && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', padding: '8px 12px', background: '#fff8e7', border: '1px dashed #d4a03a', borderRadius: '8px' }}>
                        <span style={{ fontSize: '16px' }}>🎁</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#7a4f00' }}>{item.freeGift.label}</div>
                          <div style={{ fontSize: '11px', color: '#b07d2a' }}>Free gift with 750g purchase</div>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d6a4f' }}>₹0</div>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '16px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#555', marginBottom: '10px' }}>
                    <span>Subtotal</span>
                    <span>₹{getTotal()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#555', marginBottom: '16px' }}>
                    <span>Shipping</span>
                    <span style={{ color: '#888', fontSize: '13px' }}>₹50</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '1px solid #e0e0e0', paddingTop: '16px' }}>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>Total</span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '11px', color: '#888', marginRight: '6px' }}>INR</span>
                      <span style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a' }}>₹{getTotal() + 50}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'right' }}>Including ₹{Math.round((getTotal() + 50) * 0.05)} in taxes</div>
                </div>
              </div>
            </div>

            {/* Mobile: responsive stacking */}
            <style>{`
              @media (max-width: 640px) {
                .checkout-two-col {
                  grid-template-columns: 1fr !important;
                  display: flex !important;
                  flex-direction: column-reverse !important;
                }
                .checkout-two-col > form {
                  padding: 20px 16px !important;
                  border-right: none !important;
                  border-top: 1px solid #e8e8e8 !important;
                }
                .checkout-two-col > div:last-child {
                  border-radius: 0 !important;
                  padding: 16px !important;
                }
                .checkout-header {
                  padding: 14px 16px !important;
                }
                .checkout-modal {
                  width: 100% !important;
                  max-width: 100% !important;
                  height: 100% !important;
                  max-height: 100% !important;
                  border-radius: 0 !important;
                  margin: 0 !important;
                }
              }
            `}</style>
          </div>
        </div>

      {/* ── Page Content via React Router Outlet ── */}
      <Outlet />

      {/* ── Order Confirmation Modal ── */}
      {confirmedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, padding: '16px' }}
          onClick={() => setConfirmedOrder(null)}>
          <div style={{ background: 'white', borderRadius: '24px', maxWidth: '480px', width: '100%', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', animation: 'confirmPop 0.4s cubic-bezier(0.22,1,0.36,1)' }}
            onClick={e => e.stopPropagation()}>
            <style>{`
              @keyframes confirmPop { from{opacity:0;transform:scale(0.88) translateY(24px)} to{opacity:1;transform:scale(1) translateY(0)} }
              @keyframes checkDraw { from{stroke-dashoffset:60} to{stroke-dashoffset:0} }
              @keyframes circlePulse { 0%{transform:scale(0.8);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
            `}</style>

            {/* Green header */}
            <div style={{ background: 'linear-gradient(135deg, #2d6a4f 0%, #40916c 100%)', padding: '36px 32px 28px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              {/* Decorative circles */}
              <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ position: 'absolute', bottom: '-20px', left: '-20px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

              {/* Animated checkmark */}
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '3px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', animation: 'circlePulse 0.5s ease both' }}>
                <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
                  <path d="M7 17L14 24L27 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                    style={{ strokeDasharray: 60, strokeDashoffset: 0, animation: 'checkDraw 0.5s 0.2s ease both' }} />
                </svg>
              </div>

              <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'white', margin: '0 0 6px', fontFamily: 'Georgia, serif' }}>Order Confirmed! 🎉</h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.78)', margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
                Thank you{confirmedOrder.name ? `, ${confirmedOrder.name.split(' ')[0]}` : ''}! Your order has been placed successfully.
              </p>
            </div>

            {/* Body */}
            <div style={{ padding: '28px 32px' }}>

              {/* Order ID pill */}
              <div style={{ background: '#f0faf4', border: '1.5px dashed #40916c', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#2d6a4f', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px', fontFamily: 'DM Sans, sans-serif' }}>Order ID</div>
                  <div style={{ fontSize: '20px', fontWeight: '800', color: '#1a1a1a', fontFamily: 'Georgia, serif', letterSpacing: '0.5px' }}>
                    #GUDY-{String(confirmedOrder.id).padStart(5, '0')}
                  </div>
                </div>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#d8f3dc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>📦</div>
              </div>

              {/* Order items summary */}
              <div style={{ marginBottom: '18px' }}>
                {confirmedOrder.items?.map((item, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: (!item.freeGift && i < confirmedOrder.items.length - 1) ? '1px solid #f0f0f0' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={item.image} alt={item.name} style={{ width: '38px', height: '38px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#2C1810', fontFamily: 'DM Sans, sans-serif' }}>{item.name}</div>
                          <div style={{ fontSize: '11px', color: '#999', fontFamily: 'DM Sans, sans-serif' }}>{item.weight} × {item.quantity}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#3d2009', fontFamily: 'DM Sans, sans-serif' }}>₹{item.priceINR * item.quantity}</div>
                    </div>
                    {/* FREE GIFT row in order confirmation */}
                    {item.freeGift && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', marginBottom: '8px', background: '#fff8e7', border: '1px dashed #d4a03a', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '16px' }}>🎁</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#7a4f00', fontFamily: 'DM Sans, sans-serif' }}>{item.freeGift.label}</div>
                            <div style={{ fontSize: '11px', color: '#b07d2a', fontFamily: 'DM Sans, sans-serif' }}>Free gift included in your order</div>
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#2d6a4f', fontFamily: 'DM Sans, sans-serif' }}>FREE</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#fdf6ef', borderRadius: '10px', marginBottom: '20px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#5a4a3a', fontFamily: 'DM Sans, sans-serif' }}>Total Paid (COD)</span>
                <span style={{ fontSize: '20px', fontWeight: '800', color: '#6B4423', fontFamily: 'Georgia, serif' }}>₹{confirmedOrder.totalAmount}</span>
              </div>

              {/* COD note */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: '#fff8e6', border: '1px solid #f5d87a', borderRadius: '10px', marginBottom: '24px' }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>💵</span>
                <p style={{ fontSize: '13px', color: '#7a5c00', margin: 0, lineHeight: '1.5', fontFamily: 'DM Sans, sans-serif' }}>
                  <strong>Cash on Delivery</strong> — Pay when your order arrives. 
                </p>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setConfirmedOrder(null)}
                  style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #2d6a4f 0%, #40916c 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  Continue Shopping
                </button>
                <a href={`https://wa.me/917373755589?text=Hi! I just placed Order %23GUDY-${String(confirmedOrder.id).padStart(5,'0')}. Please confirm my order.`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, padding: '14px', background: '#25D366', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  📲 WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer style={{ background: '#F5EAD8', padding: '60px 0 0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '60px', marginBottom: '50px' }}>
            <div>
              <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png" width="400px" height="160px" alt="GUDY Logo" style={{ objectFit: 'contain', marginBottom: '20px' }} />
              <p style={{ fontSize: '15px', color: '#5A4A3A', lineHeight: '1.8' }}>
                Our commitment to excellence and customer satisfaction is unwavering. Try GUDY today and experience the difference!
              </p>
            </div>
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Address</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <MapPin size={24} style={{ color: '#6B4423', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '14px', color: '#5A4A3A', lineHeight: '1.6' }}>No 364, 103-C, Narayana Pillai Street, Peramanur , Salem, Tamil Nadu – 636007</p>
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Get in touch</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Phone size={20} style={{ color: '#6B4423' }} /><span style={{ fontSize: '14px', color: '#5A4A3A' }}>+91 7373755589</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Mail size={20} style={{ color: '#6B4423' }} /><span style={{ fontSize: '14px', color: '#5A4A3A' }}>office.gudy@gmail.com</span></div>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#6B4423', margin: '24px 0 14px' }}>Follow Us</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <a href="https://www.facebook.com/profile.php?id=61588436229946" target="_blank" rel="noopener noreferrer" style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
                </a>
                <a href="https://www.instagram.com/gudy_organics/" target="_blank" rel="noopener noreferrer" style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
                </a>
                <a href="https://wa.me/917373755589" target="_blank" rel="noopener noreferrer" style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" /></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '40px', marginBottom: '50px', padding: '30px 0', borderTop: '1px solid rgba(107,68,35,0.2)', borderBottom: '1px solid rgba(107,68,35,0.2)' }}>
            {[{ icon: <Package size={32} style={{ color: '#6B4423' }} />, label: 'Express Delivery' }, { icon: <ShoppingCart size={32} style={{ color: '#6B4423' }} />, label: 'Secured payment' }, { icon: <Heart size={32} style={{ color: '#6B4423' }} />, label: 'Good Quality' }, { icon: <User size={32} style={{ color: '#6B4423' }} />, label: 'Customer Support' }].map(({ icon, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: 'white', border: '3px solid #6B4423', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#5A4A3A' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Footer Links */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '40px', paddingBottom: '40px' }}>
            <div>
              <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Categories</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Jaggery Powder', to: '/', cat: 'powder' },
                  { label: 'Jaggery Cubes', to: '/', cat: 'cubes' },
                  { label: 'Bulk Orders', to: '/contact' },
                  { label: 'About GUDY', to: '/about' },
                ].map(({ label, to, cat }) => (
                  <Link key={label} to={to} onClick={() => cat && setSelectedCategory(cat)}
                    style={{ fontSize: '14px', color: '#5A4A3A', textDecoration: 'none', borderBottom: '1px dotted rgba(90,74,58,0.3)', padding: '0 0 12px 0', display: 'block' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6B4423'}
                    onMouseLeave={e => e.currentTarget.style.color = '#5A4A3A'}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Products</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'Jaggery Powder 750G', slug: 'gudy-jaggery-powder-jar-750g-set-of-1' },
                  { label: 'Jaggery Powder 500G', slug: 'gudy-jaggery-powder-jar-500g-set-of-1' },
                  { label: 'Jaggery Powder 250G', slug: 'gudy-jaggery-powder-jar-250g-set-of-1' },
                  { label: 'Jaggery Cubes 500G', slug: 'gudy-jaggery-cubes-jar-500g-set-of-1' },
                  { label: 'Jaggery Cubes 250G', slug: 'gudy-jaggery-cubes-jar-250g-set-of-1' },
                  { label: 'Powder Set of 3 (750G)', slug: 'gudy-jaggery-powder-jar-750g-set-of-3' },
                ].map(({ label, slug }) => (
                  <Link key={slug} to={`/products/${slug}`}
                    style={{ fontSize: '14px', color: '#5A4A3A', textDecoration: 'none', borderBottom: '1px dotted rgba(90,74,58,0.3)', padding: '0 0 12px 0', display: 'block' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6B4423'}
                    onMouseLeave={e => e.currentTarget.style.color = '#5A4A3A'}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>Terms & Conditions</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'FAQ', to: '/faq' },
                  { label: 'Track Order', to: '/track-order' },
                  { label: 'Privacy Policy', to: '/privacy-policy' },
                  { label: 'Terms & Conditions', to: '/terms' },
                  { label: 'Shipping Policy', to: '/shipping-policy' },
                  { label: 'Cancellation & Refund Policy', to: '/refund-policy' },
                ].map(({ label, to }) => (
                  <Link key={to} to={to}
                    style={{ fontSize: '14px', color: '#5A4A3A', textDecoration: 'none', borderBottom: '1px dotted rgba(90,74,58,0.3)', padding: '0 0 12px 0', display: 'block' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6B4423'}
                    onMouseLeave={e => e.currentTarget.style.color = '#5A4A3A'}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6B4423', marginBottom: '20px' }}>My Account</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { label: 'My Cart', action: () => setIsCartOpen(true) },
                  { label: 'Track My Order', to: '/track-order' },
                  { label: 'Contact Support', to: '/contact' },
                ].map(({ label, to, action }) => (
                  to ? (
                    <Link key={label} to={to}
                      style={{ fontSize: '14px', color: '#5A4A3A', textDecoration: 'none', borderBottom: '1px dotted rgba(90,74,58,0.3)', padding: '0 0 12px 0', display: 'block' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#6B4423'}
                      onMouseLeave={e => e.currentTarget.style.color = '#5A4A3A'}>
                      {label}
                    </Link>
                  ) : (
                    <button key={label} onClick={action}
                      style={{ fontSize: '14px', color: '#5A4A3A', background: 'none', border: 'none', borderBottom: '1px dotted rgba(90,74,58,0.3)', padding: '0 0 12px 0', display: 'block', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#6B4423'}
                      onMouseLeave={e => e.currentTarget.style.color = '#5A4A3A'}>
                      {label}
                    </button>
                  )
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: '#E8D5BC', padding: '20px 0', marginLeft: '-40px', marginRight: '-40px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: '#5A4A3A' }}>© 2025 GUDY. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── Responsive CSS ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        /* Fix right-side whitespace WITHOUT clipping fixed-position buttons.
           overflow:clip does NOT create a new stacking context unlike overflow:hidden,
           so position:fixed children (chatbot, phone buttons) stay fully visible. */
        html { overflow-x: clip; width: 100%; }
        body { overflow-x: clip; width: 100%; max-width: 100%; }

        @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

        /* Hide mobile-only elements by default (desktop) */
        .mobile-search { display: none; }
        .mobile-actions { display: none; }
        .mobile-menu-btn { display: none; }
        .mobile-nav { display: none; }

        @media (max-width: 768px) {
          /* Header */
          .desktop-search, .desktop-actions, .desktop-nav { display: none !important; }
          .mobile-search { display: block !important; }
          .mobile-actions { display: flex !important; }
          .mobile-menu-btn { display: block !important; margin-left: 0 !important; }
          .mobile-nav { display: block !important; }
          header > div { padding: 8px 12px !important; gap: 6px !important; flex-wrap: nowrap !important; }
          header > div > div:first-child img { width: 120px !important; height: auto !important; }

          /* Products */
          .products-header { flex-direction: column !important; gap: 8px !important; align-items: flex-start !important; margin-bottom: 14px !important; }
          .mobile-filter-btn { display: block !important; width: 100% !important; }
          .mobile-filter-close { display: flex !important; }
          .desktop-sort { display: none !important; }
          .products-grid-container { grid-template-columns: 1fr !important; gap: 12px !important; }
          .filter-sidebar { display: none !important; }
          .filter-sidebar.mobile-visible { display: block !important; position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; z-index: 1000 !important; border-radius: 0 !important; height: 100vh !important; overflow-y: auto !important; margin: 0 !important; }
          .product-grid { grid-template-columns: repeat(2,1fr) !important; gap: 10px !important; }
          .product-grid > div > div:first-child { height: 155px !important; }
          .product-grid h3 { font-size: 13px !important; }
          .product-grid > div > div:last-child { padding: 12px !important; }

          /* About */
          .about-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
          .about-feature-grid { grid-template-columns: repeat(2,1fr) !important; gap: 12px !important; }
          .about-values-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
          .about-cta-btns { flex-direction: column !important; align-items: stretch !important; }
          .about-cta-btns a { text-align: center !important; }
          .about-hero { padding: 52px 16px 44px !important; }
          .about-hero h1 { font-size: 24px !important; }
          .about-hero h2 { font-size: 19px !important; }

          /* Contact */
          .contact-grid { grid-template-columns: 1fr !important; gap: 18px !important; }
          .contact-form-box { padding: 20px 14px !important; }
          .contact-strip-inner { padding: 0 12px !important; }
          .contact-strip-item { flex: 1 1 44% !important; border-right: none !important; padding: 14px 8px !important; }

          /* Policy */
          .policy-header { padding: 36px 16px 28px !important; }
          .policy-header h1 { font-size: 20px !important; line-height: 1.3 !important; }
          .policy-content { padding: 20px 14px 48px !important; }
          .policy-back-btns { flex-direction: column !important; }
          .policy-back-btns a { text-align: center !important; display: block !important; }

          /* Admin */
          .admin-expanded-grid { grid-template-columns: 1fr !important; }
          .admin-date-col { display: none !important; }

          /* Footer */
          footer { padding: 28px 0 0 !important; }
          footer > div { padding: 0 14px !important; }
          footer > div > div:first-child { grid-template-columns: 1fr !important; gap: 24px !important; }
          footer > div > div:nth-child(2) { grid-template-columns: repeat(2,1fr) !important; gap: 10px !important; padding: 18px 0 !important; }
          footer > div > div:nth-child(2) > div { flex-direction: column !important; align-items: center !important; text-align: center !important; gap: 6px !important; }
          footer > div > div:nth-child(2) > div > div:first-child { width: 44px !important; height: 44px !important; }
          footer > div > div:nth-child(3) { grid-template-columns: repeat(2,1fr) !important; gap: 20px !important; }
          footer > div > div:nth-child(4) { grid-template-columns: repeat(2,1fr) !important; gap: 20px !important; }
        }

        @media (max-width: 360px) {
          .product-grid { grid-template-columns: 1fr !important; }
          .about-feature-grid { grid-template-columns: 1fr !important; }
          footer > div > div:nth-child(2) { grid-template-columns: 1fr !important; }
          footer > div > div:nth-child(3) { grid-template-columns: 1fr !important; }
          footer > div > div:nth-child(4) { grid-template-columns: 1fr !important; }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .products-grid-container { grid-template-columns: 220px 1fr !important; }
          .product-grid { grid-template-columns: repeat(2,1fr) !important; }
          .about-values-grid { grid-template-columns: repeat(2,1fr) !important; }
          footer > div > div:nth-child(3) { grid-template-columns: repeat(2,1fr) !important; }
          footer > div > div:nth-child(4) { grid-template-columns: repeat(2,1fr) !important; }
        }

        @media (min-width: 1025px) and (max-width: 1200px) {
          .product-grid { grid-template-columns: repeat(3,1fr) !important; }
        }
      `}</style>

      {/* ── Auth Modal (Login / Signup) ── */}
      {isAuthOpen && (
        <>
          <div onClick={() => setIsAuthOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10002 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'white', borderRadius: '20px', width: '420px', maxWidth: '95vw', zIndex: 10003, padding: '36px 32px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            {/* Close */}
            <button onClick={() => setIsAuthOpen(false)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={22} color="#888" />
            </button>

            {/* Logo + title */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <img src="https://res.cloudinary.com/dzhwcv2tr/image/upload/logo_jhaizl.png" height="56px" alt="GUDY" style={{ objectFit: 'contain' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2C1810', marginTop: '12px', marginBottom: '4px' }}>
                {authMode === 'login' ? 'Welcome back' : 'Create your account'}
              </h2>
              <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                {authMode === 'login' ? 'Sign in to track orders & more' : 'Join GUDY for a better experience'}
              </p>
            </div>

            {/* Error */}
            {authError && (
              <div style={{ background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#c0392b', marginBottom: '16px' }}>
                {authError}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {authMode === 'signup' && (
                <input
                  type="text" placeholder="Full name" required
                  value={authForm.name}
                  onChange={e => setAuthForm({ ...authForm, name: e.target.value })}
                  style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '10px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                />
              )}
              <input
                type="email" placeholder="Email address" required
                value={authForm.email}
                onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '10px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
              <input
                type="password" placeholder="Password (min 6 characters)" required
                value={authForm.password}
                onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                style={{ padding: '13px 16px', border: '1.5px solid #d0d0d0', borderRadius: '10px', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />

              <button
                type="submit" disabled={authLoading}
                style={{ marginTop: '4px', padding: '14px', background: authLoading ? '#b5957a' : 'linear-gradient(135deg,#8B5A3C,#6B4423)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: authLoading ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s' }}>
                {authLoading ? 'Please wait…' : authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {/* Toggle mode */}
            <p style={{ textAlign: 'center', fontSize: '13px', color: '#666', marginTop: '20px', marginBottom: 0 }}>
              {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => { setAuthMode(authMode === 'login' ? 'signup' : 'login'); setAuthError(''); setAuthForm({ name: '', email: '', password: '' }); }}
                style={{ background: 'none', border: 'none', color: '#6B4423', fontWeight: '700', fontSize: '13px', cursor: 'pointer', padding: 0 }}>
                {authMode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </>
      )}

      {/* ── Contact Popup ── */}
      <ContactPopup isOpen={isContactPopupOpen} onClose={() => setIsContactPopupOpen(false)} />

      {/* ── Contact Float Button ── */}
      <button onClick={() => setIsContactPopupOpen(true)} style={{
        position: 'fixed',
        bottom: isMobile ? '96px' : '90px',
        right: isMobile ? '12px' : '20px',
        width: isMobile ? '48px' : '56px',
        height: isMobile ? '48px' : '56px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #8B5A3C 0%, #6B4423 100%)',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(107,68,35,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}>
        <Phone size={isMobile ? 20 : 24} />
      </button>

      {/* ── Chatbot Button ── */}
      <button onClick={() => setIsChatbotOpen(!isChatbotOpen)} style={{
        position: 'fixed',
        bottom: isMobile ? '34px' : '20px',
        right: isMobile ? '12px' : (isChatbotOpen ? '420px' : '20px'),
        display: isChatbotOpen && isMobile ? 'none' : 'flex',
        width: isMobile ? '48px' : '56px',
        height: isMobile ? '48px' : '56px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #6B4423 0%, #8B5A3C 100%)',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(107,68,35,0.4)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        transition: 'right 0.3s, transform 0.2s',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        flexShrink: 0,
      }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
        {isChatbotOpen ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      <Chatbot isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />
    </div>
  );
}